import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useMemo } from 'react';
import { WithAccountId } from '@mews-commander/types';
import { Optional } from '@mews/std';
import { DateTimeService, useTimezone } from '@mews/framework';
import {
    Amount,
    PaymentGatewaysPayloadDiscriminator,
    PaymentMethods,
    ReservationsData,
    ServiceOrderState,
    TimeUnitPeriod,
} from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { container } from '@mews-commander/legacy-app-instance';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { isBookableService } from '@mews-commander/legacy-entities/src/ducks/servicesNew';
import { useGetDefaultReservationsForLinkingSuspense } from '../../ReservationAsyncSelect/queries';
import { useGetPaymentGatewaysSuspense } from '../../../queries';
import { useGetPaymentPlansSuspense, useGetReservationsSuspense, useGetServicesSuspense } from './queries';
import { sortedReservationsByStartUtc } from './utils';

export const useGetMonthlyBookableServicesSuspense = () => {
    const enterpriseId = useCurrentEnterpriseId();
    const { data } = useGetServicesSuspense({ enterpriseId });

    return useMemo(
        () =>
            data?.services.filter(
                (service) => isBookableService(service) && service.data.value?.timeUnitPeriod === TimeUnitPeriod.Month
            ) ?? [],
        [data]
    );
};

export const useGetReservationsForMonthlyBookableServicesSuspense = ({ accountId }: WithAccountId) => {
    const services = useGetMonthlyBookableServicesSuspense();
    const servicesIds = useMemo(() => services.map(({ id }) => id), [services]);

    const enterpriseId = useCurrentEnterpriseId();
    const { data = [] } = useGetDefaultReservationsForLinkingSuspense({
        enterpriseId,
        accountId,
        showOnlyUnpaidReservations: false,
    });

    return useMemo(() => {
        const reservations = data.filter(
            ({ serviceId, serviceOrderState }) =>
                servicesIds.includes(serviceId) &&
                (serviceOrderState === ServiceOrderState.Confirmed || serviceOrderState === ServiceOrderState.Started)
        );
        return pipe(reservations, array.sort(sortedReservationsByStartUtc));
    }, [servicesIds, data]);
};

export const useGetPaymentPlansForReservationSuspense = ({
    accountId,
    serviceOrderId,
}: { serviceOrderId: Optional<string> } & WithAccountId) => {
    const paymentPlans = useGetPaymentPlansSuspense({ customerId: accountId });

    if (!serviceOrderId) {
        return [];
    }

    return paymentPlans.data?.paymentPlans?.filter(({ reservationId }) => reservationId === serviceOrderId) ?? [];
};

export const useGetOrderItemsSuspense = ({ accountId }: WithAccountId) => {
    const reservationsQuery = useGetReservationsSuspense({ accountId });
    return reservationsQuery.data.orderItems ?? [];
};

export const useGetUpcomingPaymentText = () => {
    const timezone = useTimezone();
    const { appLocalizationComponent } = container();
    const { localizeDateToString, localizeCurrencyValue } = appLocalizationComponent;

    return (nextPaymentAmount: Amount, nextPaymentDateString: string) => {
        const localizedAmount = localizeCurrencyValue(nextPaymentAmount);
        const localizedDate = localizeDateToString(
            DateTimeService.toDate(DateTimeService.fromUtcString(nextPaymentDateString, timezone))
        );

        return `${localizedAmount}${UnicodeEntity.NoBreakSpace}${UnicodeEntity.Dash}${UnicodeEntity.NoBreakSpace}${localizedDate}`;
    };
};

export const useGetAvailablePaymentPlanPaymentMethodsSuspense = ({
    reservationIds,
}: Pick<ReservationsData, 'reservationIds'>) => {
    const { data } = useGetPaymentGatewaysSuspense({
        discriminator: PaymentGatewaysPayloadDiscriminator.Reservations,
        value: { reservationIds, isForNewPaymentPlan: true },
    });

    const supportedPaymentMethods = data.paymentGateway?.supportedPaymentMethods;

    if (!supportedPaymentMethods) {
        return [];
    }

    return [
        ...(supportedPaymentMethods.sepaDirectDebit ? [PaymentMethods.SepaDirectDebit] : []),
        ...(supportedPaymentMethods.paymentCard ? [PaymentMethods.PaymentCard] : []),
    ];
};
